import { defineStore } from 'pinia'
import { tracker as openReplayTracker } from '@openreplay/tracker'
import { APP_VERSION } from '@/config/constantes'
import { Modules, IUser } from '@bc-library/interface'
import { ref } from 'vue'

type Job = {
  _created: string
  positionId: string
  startDate: string
  endDate: string
  isAlumni: boolean
  isStartDate: boolean
  officeId: string
  entityId: string
  managerId: string
  managerId2: string | null
  rate: number
  startDateLOA: string | null
  endDateLOA: string | null
  _id: string
}

type Degree = {
  _created: string
  degree: string
  endYear: number
  description: string
  isMain: boolean
  _id: string
}

type Nationality = {
  countryId: string
  passportExpirationDate: string | null
  passportIssueDate: string | null
  _id: string
}

type Display = {
  FR: string
  EN: string
}

type RadioArray = {
  _id: number
  display: Display
}

type SubAccess = {
  _id: number
  isActive: boolean
  isRadio: boolean
  radioArray: RadioArray[]
  radioValue?: number
  checkboxSource?: string
  checkboxArray?: any[]
  display: Display
  isAccess: boolean
}

type Module = {
  _id: number
  isActive: boolean
  moduleId: number
  isAccess: boolean
  subAccess: SubAccess[]
}

type AdminModule = Module
type ResourceModule = Module


type UserProfile = {
  _id: string
  isDeleted: boolean
  language: string
  isAdminProfile: boolean
  isSubAdminProfile: boolean
  isResourceProfile: boolean
  isCandidateProfile: boolean
  adminModules: AdminModule[]
  resourceModules: ResourceModule[]
  _firmId: string
  authorId: string
  display: string
  _created: string
  __v: number
  modules: Modules
}

type CurrentJob = {
  _created: string
  positionId: string
  startDate: string
  isAlumni: boolean
  isStartDate: boolean
  officeId: string
  entityId: string
  managerId: string
  managerId2: string | null
  rate: number
  startDateLOA: string | null
  endDateLOA: string | null
  _id: string
  position: {
    _id: string
    isDeleted: boolean
    authorId: string
    _firmId: string
    modifiedBy: string[]
    order: number | null
    display: string
    acronym: string
    isTemp: boolean
    isExecutive: boolean
    visibleStaffing: boolean
    visibleJobing: boolean
    visibleTimesheet: boolean
    isBudgetable: boolean
    categoryRating: number | null
    skillRating: number | null
    upperJobs: string[]
    rates: number[]
    _created: string
    skills: string[]
    __v: number
  }
  office: {
    _id: string
    isDeleted: boolean
    countryId: string
    timeZone: number
    display: string
    _firmId: string
    __v: number
  }
}

type Anonymize = {
  anonymizeDate: string | null
  anonymizeByR: string | null
  anonymizeByU: string | null
}

type ApplicationSettings = Record<string, unknown>

type FirmCountry = {
  _id: string
  display: string
  flag?: string
  acronym?: string
  weekend?: number[]
}

type FirmOffice = {
  _id: string
  display: string
  countryId: string | null
  timeZone?: number
  timeZonePopulated?: {
    _id: number
    display: string
    gmtAdjustment?: string
    winterAbbreviation?: string
    summerAbbreviation?: string
    useDaylightTime?: boolean
    timeZone?: string
    value?: number
  } | null
  region?: string
}

type FirmContext = {
  offices: FirmOffice[]
  countries: FirmCountry[]
}

type User = {
  _id: string
  shortcuts: string[]
  isOnboarded: boolean
  isOnboardedAdmin: boolean
  role: string
  picture: string
  countryId: string
  officeId: string
  isDeleted: boolean
  candidateSource: number
  invitedBy: string
  recommendedBy: string | null
  assistants: string[]
  modifiedBy: string[]
  resourceType: number
  isActive: number
  dataDuration: number
  departureDate: string | null
  isDepartureWanted: boolean
  birthdate: string
  birthPlace: string | null
  maritalStatus: string | null
  socialSecurity: string | null
  addressLine1: string
  addressLine2: string
  addressZipcode: string
  addressCity: string
  addressCountry: string
  phoneNumber1: string
  phoneNumber2: string | null
  mailPersonal: string
  wishList: string[]
  userGroups: string[]
  index: string
  isActivated: boolean
  CGUvalidatedDate: string
  mailMod: number[]
  jobs: Job[]
  firstname: string
  lastname: string
  accessProfile: string
  _firmId: string
  firm: {
    _id: string
    company: string
    isBL: boolean
    isWL: boolean
    squareLogo: string
    picture: string
    thumbPicture?: string
    onboardingQuote?: { FR?: string, EN?: string }
    onboardingQuotePhoto?: string | null
  }
  _created: string
  recruitmentDate: string
  experiences: string[]
  projects: string[]
  skills: string[]
  degrees: Degree[]
  documents: string[]
  comments: string[]
  userId: string
  __v: number
  gender: number
  username: string
  nationality: string
  givenLastname: string
  maidenname: string
  nationalities: Nationality[]
  zoneId: string[]
  anonymize: Anonymize
  applicationSettings: ApplicationSettings
  availableProfileIds: string[]
  companyZoneId: string[]
  currentJob: CurrentJob
  profile: UserProfile
  user: IUser,
  firmList: {_id: string, company: string}[]
  recruitmentSignature?: string | null
  recruitmentSignatureByDefault?: boolean
  sendRejectionMailByDefault?: boolean
  includeRejectionMailSignature?: boolean
  /** Empty/undefined = full management access; otherwise Vue route names allowed */
  managementPages?: string[]
}

export const useUserStore = defineStore('user', () => {

  const authRefreshTryed = ref(false)
  const user = ref<User>()
  const token = ref<string>()
  const firmContext = ref<FirmContext>({ offices: [], countries: [] })

  function connection({ user: newUser, token: newToken }) {

    user.value = newUser
    token.value = newToken
    authRefreshTryed.value = false
    
    if (!window?.location.host.includes('127.0.0.1') && !window?.location.host.includes('localhost')) {
      if (newUser?.user?.email) {
        openReplayTracker.setUserID(newUser.user.email)
        openReplayTracker.setMetadata('resourceId', String(newUser._id))
        openReplayTracker.setMetadata('name', `${newUser.lastname} ${newUser.firstname}`)
        if (newUser.profile?.display) openReplayTracker.setMetadata('profile', newUser.profile.display)
        if (newUser.firm?.company) openReplayTracker.setMetadata('firm', newUser.firm.company)
        if (newUser._firmId) openReplayTracker.setMetadata('_firmId', String(newUser._firmId))
        if (newUser.currentJob?.positionId) openReplayTracker.setMetadata('jobId', String(newUser.currentJob.positionId))
        if (newUser.currentJob?.position?.display) openReplayTracker.setMetadata('job', newUser.currentJob.position.display)
        if (newUser.picture) openReplayTracker.setMetadata('picture', newUser.picture)
        openReplayTracker.setMetadata('release', APP_VERSION)
      }
    }
    

    return { user }
  }

  const disconnection = () => {
    authRefreshTryed.value = false
    user.value = undefined
    token.value = undefined
    firmContext.value = { offices: [], countries: [] }
  }

  const setFirmContext = (context?: Partial<FirmContext>) => {
    firmContext.value = {
      offices: context?.offices || [],
      countries: context?.countries || [],
    }
  }

  const switchUserProfile = (profile, newAvailableProfiles) => {
    user.value.profile = profile
    user.value.accessProfile = profile._id
    user.value.availableProfileIds = newAvailableProfiles.map(p => p._id)
    user.value.availableProfiles = newAvailableProfiles;
  }

  return {
    authRefreshTryed,
    user,
    token,
    firmContext,
    connection,
    disconnection,
    setFirmContext,
    switchUserProfile,
  }
},
  {
    persist: true,
  }
)
